// Comparison-item identifiers shared by the URL state, the picker, and the
// backend's `/api/diff?items=…` contract.
//
// Two item types exist (see ../backend/AGENTS.md and AGENTS.md):
//
//   app:<name>
//   chart:<repoSlug>/<chart>@<version>
//
// The chart form embeds a slash and an `@`, both of which are unreserved
// URL characters so the comparison view stays human-readable. To make the
// embed safe even when `repoSlug` itself contains slashes (which it can
// after `slugify_repo_url` collapses paths), the parser splits on the
// **last** `@` and the **last** `/` before it.

export const APP_PREFIX = "app:";
export const CHART_PREFIX = "chart:";

export interface AppItem {
  type: "app";
  raw: string;
  name: string;
}

export interface ChartItem {
  type: "chart";
  raw: string;
  repoSlug: string;
  chart: string;
  version: string;
}

export type ParsedItem = AppItem | ChartItem;

export class ItemIdParseError extends Error {
  constructor(public readonly id: string, reason: string) {
    super(`item id ${JSON.stringify(id)} is malformed: ${reason}`);
    this.name = "ItemIdParseError";
  }
}

/** Parse a single comparison-item id; throws if the input is malformed. */
export function parseItemId(input: string): ParsedItem {
  const id = (input ?? "").trim();
  if (!id) {
    throw new ItemIdParseError(input, "id is empty");
  }

  if (id.startsWith(APP_PREFIX)) {
    const name = id.slice(APP_PREFIX.length);
    if (!name) {
      throw new ItemIdParseError(id, "app name is empty");
    }
    return { type: "app", raw: id, name };
  }

  if (id.startsWith(CHART_PREFIX)) {
    const body = id.slice(CHART_PREFIX.length);
    const atIndex = body.lastIndexOf("@");
    if (atIndex <= 0 || atIndex === body.length - 1) {
      throw new ItemIdParseError(id, "chart id needs '<repoSlug>/<chart>@<version>'");
    }
    const version = body.slice(atIndex + 1);
    const left = body.slice(0, atIndex);
    const slashIndex = left.lastIndexOf("/");
    if (slashIndex <= 0 || slashIndex === left.length - 1) {
      throw new ItemIdParseError(id, "chart id needs '<repoSlug>/<chart>'");
    }
    const repoSlug = left.slice(0, slashIndex);
    const chart = left.slice(slashIndex + 1);
    return { type: "chart", raw: id, repoSlug, chart, version };
  }

  throw new ItemIdParseError(id, "unknown prefix (expected 'app:' or 'chart:')");
}

/** Variant that returns null instead of throwing — handy for URL parsing. */
export function tryParseItemId(input: string): ParsedItem | null {
  try {
    return parseItemId(input);
  } catch {
    return null;
  }
}

export function formatAppId(name: string): string {
  if (!name) {
    throw new ItemIdParseError(name, "app name is empty");
  }
  return `${APP_PREFIX}${name}`;
}

export function formatChartId(
  repoSlug: string,
  chart: string,
  version: string,
): string {
  if (!repoSlug || !chart || !version) {
    throw new ItemIdParseError(
      `chart:${repoSlug}/${chart}@${version}`,
      "repoSlug, chart, and version must be non-empty",
    );
  }
  return `${CHART_PREFIX}${repoSlug}/${chart}@${version}`;
}

/** True when the id is recognised by `parseItemId`. */
export function isItemId(input: string): boolean {
  return tryParseItemId(input) !== null;
}
